import type { JSX } from "react";
import { useEffect, useState } from "react";
import { getModels, type ChatModel } from "../../lib/api";

const STORE_KEY = "cs.chatModel";

/** The model the user picked, read at send time. Undefined → server default. */
export function getSelectedModelId(): string | undefined {
  const v = localStorage.getItem(STORE_KEY);
  return v !== null && v.length > 0 ? v : undefined;
}

interface ModelSelectorProps {
  /** Whether the picker UI is enabled (FEATURE_MODEL_SELECTOR). Off → show the
   * configured default model's name as plain text, no dropdown. Default true. */
  enabled?: boolean;
  /** The default model's display name (health.llmModelName), shown as plain text
   * when the picker is off — no /api/models fetch needed in that case. */
  defaultModelName?: string | undefined;
}

/**
 * Chat model selector. Lists the server-configured selectable models
 * (GET /api/models) and persists the choice in localStorage; the send path
 * reads it via getSelectedModelId(). Hidden when only one (or no) model exists.
 * When `enabled` is false (FEATURE_MODEL_SELECTOR off) it renders the default
 * model's name as plain text — no picker — and never mutates the stored choice.
 */
export function ModelSelector({ enabled = true, defaultModelName }: ModelSelectorProps = {}): JSX.Element | null {
  const [models, setModels] = useState<ChatModel[]>([]);
  const [selected, setSelected] = useState<string>(() => localStorage.getItem(STORE_KEY) ?? "");

  useEffect(() => {
    if (!enabled) return; // picker off — no need to fetch the model list at all
    void getModels().then((ms) => {
      setModels(ms);
      setSelected((cur) => {
        if (cur.length > 0 && ms.some((m) => m.id === cur)) return cur;
        const first = ms.find((m) => m.available)?.id ?? ms[0]?.id ?? "";
        if (first.length > 0) localStorage.setItem(STORE_KEY, first);
        return first;
      });
    });
  }, [enabled]);

  // Picker off: show the configured default model's name (plain text), straight
  // from health — no /api/models fetch.
  if (!enabled) {
    return defaultModelName ? <span className="model-selector-name">{defaultModelName}</span> : null;
  }

  if (models.length === 0) return null;
  // With a single model there is nothing to pick — show its name inline so the
  // merged status pill still reads "…provider/<model> · 健康".
  if (models.length === 1) return <span className="model-selector-name">{models[0]?.name}</span>;

  return (
    <label className="model-selector has-tip" data-tip="选择对话模型">
      <select
        className="model-selector-select"
        value={selected}
        aria-label="对话模型"
        onChange={(e) => {
          setSelected(e.target.value);
          localStorage.setItem(STORE_KEY, e.target.value);
        }}
      >
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
            {m.available ? "" : "(未配置)"}
          </option>
        ))}
      </select>
    </label>
  );
}
